import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const VET_COLORS = ['#0f4c5c', '#f97316', '#10b981'];
const PIE_COLORS = ['#0f4c5c', '#f97316', '#10b981'];

const RevenueBar = ({ label, value, max, currency = 'JOD' }) => {
    const [width, setWidth] = React.useState(0);
    const target = max > 0 ? (value / max) * 100 : 0;

    React.useEffect(() => {
        const timer = setTimeout(() => setWidth(target), 700);
        return () => clearTimeout(timer);
    }, [target]);

    return (
        <div className="clinic-dashboard-revbar">
            <span className="clinic-dashboard-revbar-label">{label}</span>
            <div className="clinic-dashboard-revbar-track">
                <div
                    className="clinic-dashboard-revbar-fill"
                    style={{ width: `${width}%`, transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
            </div>
            <span className="clinic-dashboard-revbar-value">{value} {currency}</span>
        </div>
    );
};

const VeterinarianProductivity = ({ onBack, currency = 'JOD' }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [data, setData] = useState({
        kpis: [],
        revenueBars: [],
        revenueTimeData: [],
        salesPie: []
    });

    useEffect(() => {
        const fetchProductivity = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/productivity?year=${filters.year}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching veterinarian productivity:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchProductivity();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading productivity report...</div>;
    }

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        return chartData.slice(0, now.getMonth() + 1);
    };

    const revenueTimeData = filterMonthlyChartData(data.revenueTimeData, filters.year);
    const maxRevenue = data.revenueBars.length > 0 ? Math.max(...data.revenueBars.map(b => b.value)) : 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader
                title="Veterinarian Productivity"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Top row */}
            <div style={{ display: 'flex', gap: '16px' }}>
                {/* Key metrics */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row four">
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Average revenue by visit — per doctor */}
                <div className="clinic-dashboard-section-card" style={{ flex: 0.7, display: 'flex', flexDirection: 'column', justifyContent: 'center', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Average revenue by visit</div>
                    {maxRevenue > 0 ? (
                        <div className="clinic-dashboard-revbar-list">
                            {data.revenueBars.map((bar, i) => (
                                <RevenueBar key={i} {...bar} max={maxRevenue} currency={currency} />
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No revenue data for this period" />
                    )}
                </div>
            </div>

            {/* Bottom row */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Revenue overtime */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Revenue overtime</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={revenueTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="medical services" stroke={VET_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
                                <Line animationBegin={600} type="monotone" dataKey="non medical services" stroke={VET_COLORS[1]} strokeWidth={2} dot={{ r: 3 }} />
                                <Line animationBegin={600} type="monotone" dataKey="medications" stroke={VET_COLORS[2]} strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Sales pie */}
                <div className="clinic-dashboard-section-card" style={{ flex: 0.6, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Sales</div>
                    {data.salesPie.some(s => s.value > 0) ? (
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <ResponsiveContainer width="100%" height="75%">
                                <PieChart>
                                    <Pie
                                        data={data.salesPie}
                                        outerRadius={75}
                                        paddingAngle={1}
                                        dataKey="value"
                                        stroke="#fff"
                                        strokeWidth={1}
                                    >
                                        {data.salesPie.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="clinic-dashboard-legend" style={{ flexWrap: 'wrap', justifyContent: 'center', gap: '8px 12px' }}>
                                {data.salesPie.map((entry, index) => (
                                    <div key={index} className="clinic-dashboard-legend-item">
                                        <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                        <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EmptyState message="No sales data for this period" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VeterinarianProductivity;
